import { useEffect, useState } from "react"
import { ProjectProps } from "../../../interfaces/project_types"
import { Error_Component } from "../../misc"
import { useAuth } from "../../../App"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useFormData } from "@/components/_Shared/Form/Hooks/useFormDataFetch"
import { ProjectFormSchema } from "./Interface/DefaultSchema"
import { ImportFormProps } from "./Interface/ImportFormProps"
import { DefaultProps } from "./Interface/DefaultProps"
import { ProjectFormBase } from "./ProjectFormBase"
import { createProject, updateProject } from "@/api/projects"
import {Alert} from "@/components/_Shared/Form/Buttons/Alert"
import { InvoiceFormContainer } from "./InvoiceForm/InvoiceFormContainer"
import { formatProjectData } from "./Utils/formatProjectData"
import { CreateOrUpdate } from "@/components/_Shared/Form/Utils/MethodHandler"
import {getInvoiceByProjectId} from "@/api/invoices.ts";
import { EmployeeProps } from "@/interfaces/employee_type"

/**
 * This component shows the user the form to create or update a new project.
 * 
 * @param FormProps : The ProjectProp object that is used to fill the form (If updating)
 * @param method : The method to use to submit the form (POST or PUT)
 * 
 */
export function ProjectForm(
    {formProps, method}: {formProps?: ProjectProps, method: "POST" | "PUT"}
) {
    const [hasFolderCreated, setHasFolderCreated] = useState(false) // Used to check if the folder was created successfully (when creating a project)
    const { user } = useAuth()

    const { 
        register, 
        handleSubmit, 
        formState: { errors, isSubmitSuccessful, isSubmitting },
        control,
        getValues,
        watch,
        setError,
    } = useForm<z.infer<typeof ProjectFormSchema>>({
        resolver: zodResolver(ProjectFormSchema),
        defaultValues: formProps ? ImportFormProps({formProps}) : DefaultProps({user: user as EmployeeProps})
    })

    const watch_Status = watch("status")
    const watch_ProjectName = watch("project_name")

    const { projectManagers, clients, cities, point_of_contacts, fetchAllDataProject } = useFormData()

    const onSubmit = async (data: z.infer<typeof ProjectFormSchema>) => {
        const project_data = formatProjectData(data, formProps?.project_id)
        const method_handler = CreateOrUpdate(method, createProject, updateProject)

        if (method_handler) {
            const response = await method_handler(project_data)

            if (response.status === 400) {
                setError("root", {message: "An error occurred while submitting the project form."})
            } else if (response.status === 403) {
                setError("root", {message: "You are not authorized to submit this project."})
            } else if (response.status === 500) {
                setError("root", {message: "An error occurred while submitting the project form."})
            } 

            // Used to check if the folder was created successfully (when creating a project)
            // If not, we need to alert the user that the folder was not created successfully
            if (response.data && 'folder_created' in response.data) {
                setHasFolderCreated(response.data.folder_created)
            }
        } else {
            console.error("Method handler not found")
            setError("root", {message: "An error occurred while submitting the project form."})
        }
    }
    
    useEffect(() => {
        fetchAllDataProject()
    }, [fetchAllDataProject])

    useEffect(() => {
        const check = async () => {
            // Used to check if the user is attempting to close a project without sending at least one invoice
            if (await checkCompletedUpdateForInvoice(method, formProps?.project_id || "") && watch_Status?.value === "Completed")
                    setError("root", {message: "No invoice for this project has been detected. Are you sure you want to mark this project as complete?"})

            if (watch_ProjectName !== formProps?.project_name && method === "PUT") {
                setError("project_name", {message: "Warning: When changing the project name, the folder location will also be changed to match the new project name. Please ensure you also manually update the folder name on the server so that it matches the new project name."})
            }
        }

        check()
    }, [watch_Status, watch_ProjectName, method, formProps?.project_id, setError])

    return (
    <>
        {isSubmitSuccessful && 
            <Alert
                title="Success"
                description={setDescription(method, hasFolderCreated)}
                redirects={
                    [
                        {label: "To Projects", value: "/projects"},
                        {label: "To Dashboard", value: "/main_menu"}
                ]
                } />}

        {errors.root && <Error_Component errorString={errors.root.message || "An error occurred"} />}
                
        <ProjectFormBase 
            errors={errors}
            register={register} 
            control={control} 
            manager_list={projectManagers} 
            client_list={clients} 
            city_list={cities} 
            point_of_contact_list={point_of_contacts}
            method={method}
            onSubmit={handleSubmit(onSubmit)}
            isProcessing={isSubmitting}
            project_id={formProps?.project_id ?? ""}
            folder_path={formProps?.folder_location ?? ""}
        />

        {method === "PUT" && <InvoiceFormContainer 
            project_id={formProps?.project_id ?? ""}
            user_email={user?.email ?? ""}
            project_on_submit={() => onSubmit(getValues())}
        />}

    </>
    )
}

// Used to check if the user is attempting to close a project without sending at least one invoice
async function checkCompletedUpdateForInvoice(method: "POST" | "PUT", project_id: string): Promise<boolean> {
    if (method === "PUT" && project_id !== "") {
        const response = await getInvoiceByProjectId(project_id)

        // Vitest complains when I don't put this here...
        if (response.operation == "COMPLETED") {
            return response.data.length <= 0;
        } else {
            return false
        }
    } else {
        return false
    }
}

// Just an easy way to set the description of the alert based on the method and if the folder was created successfully
function setDescription(method: "POST" | "PUT", folder_created: boolean) {
    if (method === "POST" && folder_created) {
        return "The project has been created successfully."
    } else if (method === "POST" && !folder_created) {
        return "The project has been created successfully, however, there was an error creating the folder. Please manually create the folder."
    } else if (method === "PUT") {
        return "The project has been updated successfully."
    } else {
        return "An error occurred while submitting the project form."
    }
}